import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import WidgetBox, { Props as WidgetBoxProps } from '../WidgetBox';
import { withModuleData, WithModuleDataProps } from '../../_hoc/withModuleData';
import reduxConnectContainer from './ExhibitionistBox.container';
import ConfigPopover from '../../_partials/ConfigPopover';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { GallectionType } from '../../../../../types/gallection';
import s from './ExhibitionistBox.scss';

const ExhibitionistEditForm = lazy(
  () => import(/* webpackChunkName: "owner" */ './ExhibitionistEditForm')
);

export enum ExhibitionistBoxType {
  TALL = 'tall',
  STRIP = 'strip',
}

export interface Props {
  streamId: string;
  deviations: PapiDeviation[];
  moduleId: number;
  exhibitionistType: ExhibitionistBoxType;
  title: string;
  profileOwnerUser: PapiUser;
  seeAllUrl?: string;
  isEmpty?: boolean;
  hasMore?: boolean;
  emptyView?: React.ReactNode;
  headerExtras?: WidgetBoxProps['headerExtras'];
  children: (boxProps) => React.ReactNode;
}

const defaultProps = {
  placement: 'bottom',
};

export type ExhibitionistBoxProps = Props &
  WidgetBoxProps &
  WithModuleDataProps &
  WithTranslation;

const enhance = compose(
  reduxConnectContainer,
  withModuleData,
  withTranslation()
);
class ExhibitionistBox extends React.Component<ExhibitionistBoxProps> {
  static defaultProps = defaultProps;
  render() {
    const {
      t,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      headerExtras,
      emptyView,
      children,
      moduleData: {
        sourceType,
        viewMode,
        slideImageFit,
        premiumFolderData,
        folderId,
      },
      isCurrentUserProfileOwner,
      section,
      zone,
      ...props
    } = this.props;

    const isCollection = sourceType === GallectionType.COLLECTION;
    const defaultTitle = this.getDefaultTitle(sourceType);

    return (
      <WidgetBox
        section={section}
        zone={zone}
        header={
          <>
            <PremiumFolderDataIcon
              premiumFolderData={premiumFolderData}
              size={IconSize.COMPACT}
              className={s['premium-folder-icon']}
            />
            {props.title || defaultTitle}
          </>
        }
        headerExtras={headerExtras}
        hasMore={props.hasMore}
        seeAllUrl={props.seeAllUrl}
        widgetMenuRenderer={this.renderEditMenu}
        moduleId={props.moduleId}
        widgetMoveUp={widgetMoveUp}
        widgetMoveDown={widgetMoveDown}
        widgetRemove={widgetRemove}
        isEmpty={props.isEmpty}
        emptyView={emptyView}
      >
        <OverlayContextProvider
          value={{
            showAuthorTooltip: isCollection,
          }}
        >
          {children({
            ...props,
            sourceType,
            viewMode,
            slideImageFit,
            premiumFolderData,
            folderId,
          })}
        </OverlayContextProvider>
      </WidgetBox>
    );
  }

  getDefaultTitle = (sourceType: GallectionType) => {
    const { t } = this.props;

    // widgets.exhibitionist.source.featured_gallery
    // widgets.exhibitionist.source.featured_collection
    // widgets.exhibitionist.source.newest
    // widgets.exhibitionist.source.gallery
    // widgets.exhibitionist.source.prints
    // widgets.exhibitionist.source.collection
    return t(`widgets.exhibitionist.source.${sourceType}`);
  };

  renderEditMenu = closeMenu => {
    return (
      <ConfigPopover width={266} onClose={closeMenu} withPaddings={false}>
        <Suspense fallback={<LoadingIndicator />}>
          <ExhibitionistEditForm
            moduleId={this.props.moduleId}
            exhibitionistType={this.props.exhibitionistType}
            close={closeMenu}
          />
        </Suspense>
      </ConfigPopover>
    );
  };
}

export default enhance(ExhibitionistBox);
